import {Environment, graphql} from 'react-relay';
import {MutationConfig, RecordSourceSelectorProxy} from 'relay-runtime';

import Util from '../forecast-app/shared/util/util';
import {trackAndCommitMutation} from '../tracking/amplitude/TrackMutation';
import {
	UnassignPersonFromTaskInput,
	UnassignPersonFromTaskMutation,
} from './__generated__/UnassignPersonFromTaskMutation.graphql';
import {MutationInput, RelayOnCompleted, RelayOnError} from './MutationTypes';
import type {Disposable} from 'relay-runtime';

const mutation = graphql`
	mutation UnassignPersonFromTaskMutation($input: UnassignPersonFromTaskInput!) {
		unassignPersonFromTask(input: $input) {
			task {
				...AssignPersonToTaskMutation_task @relay(mask: false)
			}
		}
	}
`;

// Imperative updater since assignedPersons does not follow relay connection paradigm. With a connection, RANGE_DELETE would be usable instead.
// Alternatively could pass in full list of assignees and manipulate with standard optimistic updater, but feels more correct to allow mutation to run in isolation.
const optimisticUpdater = (input: MutationInput<UnassignPersonFromTaskInput>, store: RecordSourceSelectorProxy) => {
	if (!input.taskId || !input.personId) return;

	const taskNode = store.get(input.taskId);
	const existingAssignedPersons = taskNode?.getLinkedRecords('assignedPersons') ?? [];
	const removedAssignedPerson = store.get(input.personId);
	if (removedAssignedPerson) {
		const newAssignedPersons = existingAssignedPersons.filter(personRecord => personRecord !== removedAssignedPerson);
		taskNode?.setLinkedRecords(newAssignedPersons, 'assignedPersons');
	}
};

function commit(
	environment: Environment,
	input: MutationInput<UnassignPersonFromTaskInput>,
	onSuccess: RelayOnCompleted<UnassignPersonFromTaskMutation>,
	onError: RelayOnError
): Disposable {
	const mutationConfig: MutationConfig<UnassignPersonFromTaskMutation> = {
		mutation,
		variables: {input: {csrfToken: Util.getCsrfValue(), socketClientId: Util.getClientId(), ...input}},
		optimisticUpdater: store => optimisticUpdater(input, store),
		onCompleted: onSuccess,
		onError: onError,
	};
	return trackAndCommitMutation(environment, mutationConfig);
}

export default {commit};
